/** @jsxImportSource @emotion/react */
'use memo'

import React, { useState } from 'react'
import css from '@emotion/css/macro'
import { Tooltip } from 'antd'
import { EditOutlined, UsergroupAddOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import appOrigin from '@sportninja/common/constants/appOrigin'
import ShareIcon from 'src/components/icons/ShareIcon'
import ShareModal from 'src/components/ShareModal'
import { paragraphXSmall, weightMedium } from 'src/components/css'
import { ProfilePlayer } from '../types'

interface Props {
  onEditPlayer: () => void
  onOpenFamily: () => void
  player: ProfilePlayer | null
  /**
   * Whether the current user is PRIMARY (admin) for this player. Only a primary
   * may edit the player's profile, so the Edit Player button is hidden
   * otherwise. Defaults to false.
   */
  canEdit?: boolean
}

const baseButtonStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
  ${paragraphXSmall}
  ${weightMedium}
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.85;
  }

  .anticon {
    font-size: 16px;
  }
`

const editButtonStyle = css`
  ${baseButtonStyle}
  background-color: ${colors.PRIMARY};
  color: ${colors.SECONDARY_300};
  border: none;
`

const outlinedButtonStyle = css`
  ${baseButtonStyle}
  background-color: transparent;
  color: ${colors.NEUTRAL_0};
  border: 1px solid ${colors.PRIMARY};
`

// Icon-only outlined button (Family, Share) — square, no label.
const iconButtonStyle = css`
  ${outlinedButtonStyle}
  width: 32px;
  height: 32px;
  padding: 0;
`

const ProfilePlayerActions: React.FC<Props> = ({
  onEditPlayer,
  onOpenFamily,
  player,
  canEdit = false,
}) => {
  const [shareOpen, setShareOpen] = useState(false)

  const playerName = player
    ? `${player.name_first ?? ''} ${player.name_last ?? ''}`.trim() || 'Player'
    : 'Player'
  const shareUrl = player ? `${appOrigin}/player/${player.id}` : ''

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 8px;
      `}
    >
      {/* Edit Player — only a primary (admin) for this player may edit. */}
      {canEdit ? (
        <button type='button' css={editButtonStyle} onClick={onEditPlayer}>
          <EditOutlined />
          {capitalizeWords('Edit Player')}
        </button>
      ) : null}

      <button
        type='button'
        css={outlinedButtonStyle}
        onClick={onOpenFamily}
        disabled={!player}
        aria-label='Family management'
      >
        <UsergroupAddOutlined />
        {capitalizeWords('Family Management')}
      </button>

      <Tooltip title={capitalizeWords('Share Player')}>
        <button
          type='button'
          css={iconButtonStyle}
          onClick={() => setShareOpen(true)}
          disabled={!player}
          aria-label='Share player'
        >
          <ShareIcon size={16} color={colors.NEUTRAL_0} />
        </button>
      </Tooltip>

      {/* Overflow (⋮) menu hidden until its actions are defined. To re-enable,
          add a MoreOutlined import + an icon-only outlined button reusing
          iconButtonStyle. */}

      <ShareModal
        isOpen={shareOpen}
        onClose={() => setShareOpen(false)}
        entityType='player'
        entityName={playerName}
        shareUrl={shareUrl}
      />
    </div>
  )
}

export default ProfilePlayerActions
